import { MessageBarButton } from '@fluentui/react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import * as React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useHorizontalStackStyles } from '../../themes/styles/flexbox-styles';

export interface ErrorBannerActionProps {
    title: string;
    onClick: () => void;
}

interface ErrorBannerActionsProps {
    onRetry?: () => void;
    onSeeDetails?: () => void;
    retryable?: boolean;
    additionalActions?: ErrorBannerActionProps[];
}

const messages = defineMessages({
    retryButtonAriaLabel: {
        id: 'ErrorBannerActions_RetryButton_AriaLabel',
        defaultMessage: 'Retry',
        description: 'Aria label of the "Retry" button in the error banner',
    },
    seeDetailsButtonAriaLabel: {
        id: 'ErrorBannerActions_SeeDetailsButton_AriaLabel',
        defaultMessage: 'See details',
        description: 'Aria label of the "See details" button in the error banner',
    },
});

/**
 * Styles
 */

const useErrorStyles = makeStyles({
    root: {
        gap: '8px',
    },
});

// Fluent v8 MessageBar keeps a light surface in both themes, so a theme-dependent focus token can become white-on-white.
const messageBarFocusOutlineColor = '#000000';

const useActionButtonStyles = makeStyles({
    root: {
        ':focus-visible': {
            outline: `2px solid ${messageBarFocusOutlineColor}`,
            outlineOffset: '2px',
        },
        '@media (forced-colors: active)': {
            ':focus-visible': {
                outlineColor: 'Highlight',
            },
        },
    },
});

/**
 * End Styles
 */

export const ErrorBannerActions: React.FC<ErrorBannerActionsProps> = React.memo((props: ErrorBannerActionsProps) => {
    const { onRetry, onSeeDetails, retryable, additionalActions } = props;

    // Intl hooks
    const { formatMessage } = useIntl();

    //Style hooks
    const horizontalStackStyles = useHorizontalStackStyles();
    const errorStyles = useErrorStyles();
    const actionButtonStyles = useActionButtonStyles();

    const showSeeDetailsButton = !!onSeeDetails;
    const showRetryButton = retryable && onRetry;

    // Render nothing rather than an empty stack if no buttons would end up being rendered
    if (!showSeeDetailsButton && !showRetryButton && !additionalActions) {
        return <></>;
    }

    return (
        <div className={mergeClasses(horizontalStackStyles.root, errorStyles.root)}>
            {onSeeDetails && (
                <div className={horizontalStackStyles.item}>
                    <MessageBarButton
                        ariaLabel={formatMessage(messages.seeDetailsButtonAriaLabel)}
                        className={actionButtonStyles.root}
                        onClick={onSeeDetails}
                    >
                        <FormattedMessage
                            id="ErrorBannerActions_SeeDetailsButton_Text"
                            defaultMessage="See details"
                            description="Text of the 'See details' button in the error banner"
                        />
                    </MessageBarButton>
                </div>
            )}

            {retryable && onRetry && (
                <div className={horizontalStackStyles.item}>
                    <MessageBarButton
                        ariaLabel={formatMessage(messages.retryButtonAriaLabel)}
                        className={actionButtonStyles.root}
                        onClick={onRetry}
                    >
                        <FormattedMessage
                            id="ErrorBannerActions_RetryButton_Text"
                            defaultMessage="Retry"
                            description="Text of the 'Retry' button in the error banner"
                        />
                    </MessageBarButton>
                </div>
            )}

            {additionalActions &&
                additionalActions.map((action, index) => (
                    <div className={horizontalStackStyles.item} key={`additional-action-${index}`}>
                        <MessageBarButton
                            ariaLabel={action.title}
                            className={actionButtonStyles.root}
                            onClick={action.onClick}
                        >
                            {action.title}
                        </MessageBarButton>
                    </div>
                ))}
        </div>
    );
});

export default ErrorBannerActions;
